import { createBrowserRouter, Navigate, Outlet, useLocation, useOutletContext, type RouteObject } from 'react-router';
import type { PortalRole, Session } from '@product-portal/shared';
import { useSession } from '@/hooks/use-session';
import { BusinessLayout, GuardedSpaceLayout, type RouteHandle } from '@/layouts/space-layout';
import { AccessDeniedPage } from '@/pages/access-denied-page';
import { DevLoginPage } from '@/pages/dev-login-page';
import { LoadingScreen } from '@/pages/loading-screen';
import { LoginPage } from '@/pages/login-page';
import { NotFoundPage } from '@/pages/not-found-page';
import { HomePage } from '@/pages/home-page';
import { NewRequestPage } from '@/pages/new-request-page';
import { ProductsPage } from '@/pages/products-page';
import { ReleaseNotesPage } from '@/pages/release-notes-page';
import { RequestPage } from '@/pages/request-page';
import { RequestsPage } from '@/pages/requests-page';
import { TeamPrioritiesPage } from '@/pages/team-priorities-page';
import { QualificationQueuePage } from '@/pages/product/qualification-queue-page';
import { QualifyPage } from '@/pages/product/qualify-page';
import { OnBehalfPage } from '@/pages/product/on-behalf-page';
import { BacklogPage } from '@/pages/product/backlog-page';
import { ReleaseNoteEditorPage } from '@/pages/product/release-note-editor-page';
import { TeamsPage } from '@/pages/admin/teams-page';
import { UsersPage } from '@/pages/admin/users-page';
import { ProductsAdminPage } from '@/pages/admin/products-page';
import { AuditPage } from '@/pages/admin/audit-page';

function useSessionContext(): Session {
  return useOutletContext<Session>();
}

/**
 * Signed-in area. Without a session → login. A Demandeur or a Référent
 * without team → « Accès refusé » and nothing else, whatever the URL (the API
 * answers NO_TEAM anyway). A PO without team is let in (ADR-005 §1).
 */
function SessionGate() {
  const session = useSession();
  const location = useLocation();
  if (session.isPending) return <LoadingScreen />;
  if (session.isError) return <p className="p-8 text-sm text-muted-foreground">Le portail est momentanément indisponible.</p>;
  if (!session.data) {
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/connexion?returnTo=${returnTo}`} replace />;
  }
  if (session.data.deniedForNoTeam) return <AccessDeniedPage session={session.data} reason="no_team" />;
  return <Outlet context={session.data} />;
}

function BusinessRoute() {
  const session = useSessionContext();
  return <BusinessLayout session={session} />;
}

function ProductRoute() {
  return <GuardedSpaceLayout space="product" session={useSessionContext()} />;
}

function AdminRoute() {
  return <GuardedSpaceLayout space="admin" session={useSessionContext()} />;
}

function HomeRoute() {
  return <HomePage session={useSessionContext()} />;
}

function RequestRoute() {
  return <RequestPage currentUserId={useSessionContext().user.id} />;
}

/** Display-only role gate inside the business space; the API enforces the real rule. */
function RoleGate({ roles, children }: { roles: PortalRole[]; children: React.ReactNode }) {
  const session = useSessionContext();
  return roles.includes(session.role) ? <>{children}</> : <AccessDeniedPage session={session} reason="forbidden" />;
}


const wide: RouteHandle = { wide: true };

export const routes: RouteObject[] = [
  { path: '/connexion', element: <LoginPage /> },
  { path: '/connexion/dev', element: <DevLoginPage /> },
  {
    element: <SessionGate />,
    children: [
      {
        element: <BusinessRoute />,
        children: [
          { index: true, element: <HomeRoute /> },
          { path: 'demandes', element: <RequestsPage /> },
          { path: 'demandes/nouvelle', element: <NewRequestPage /> },
          { path: 'demandes/:id', element: <RequestRoute /> },
          { path: 'produits', element: <ProductsPage /> },
          { path: 'release-notes', element: <ReleaseNotesPage /> },
          {
            // Screen 06: display gate only — GET /api/team-priorities is what refuses a Demandeur.
            path: 'mon-equipe',
            element: (
              <RoleGate roles={['referent', 'po']}>
                <TeamPrioritiesPage />
              </RoleGate>
            ),
          },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
      {
        path: 'produit',
        element: <ProductRoute />,
        children: [
          { index: true, element: <Navigate to="qualification" replace /> },
          { path: 'qualification', handle: wide, element: <QualificationQueuePage /> },
          { path: 'demandes/:id', element: <QualifyPage /> },
          { path: 'saisie', element: <OnBehalfPage /> },
          { path: 'backlog', element: <BacklogPage /> },
          { path: 'release-notes', element: <ReleaseNoteEditorPage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
      {
        path: 'admin',
        element: <AdminRoute />,
        children: [
          { index: true, element: <Navigate to="equipes" replace /> },
          { path: 'equipes', element: <TeamsPage /> },
          { path: 'utilisateurs', element: <UsersPage /> },
          { path: 'produits', element: <ProductsAdminPage /> },
          { path: 'journal', element: <AuditPage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];


export function createRouter() {
  return createBrowserRouter(routes);
}
